
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { format, addDays, isBefore } from "date-fns";

// Subscription plans and their limits
export const SUBSCRIPTION_PLANS = {
  FREE: {
    name: "Free Trial",
    videosPerDay: 3,
    messagesPerVideo: 10,
    maxVideoDuration: 15, // minutes
  },
  ESSENTIAL: {
    name: "Essential",
    videosPerDay: 30,
    messagesPerVideo: 30,
    maxVideoDuration: 45, // minutes
  },
  GROWTH: {
    name: "Growth",
    videosPerDay: 100,
    messagesPerVideo: 100,
    maxVideoDuration: 120, // minutes
  },
  PRO: {
    name: "Pro",
    videosPerDay: 999999, // effectively unlimited
    messagesPerVideo: 999999, // effectively unlimited
    maxVideoDuration: 180, // minutes
  },
};

export type SubscriptionPlan = 'FREE' | 'ESSENTIAL' | 'GROWTH' | 'PRO';
export type PaymentStatus = 'active' | 'expired' | 'pending';

export interface UserSubscription {
  id: string;
  user_id: string;
  subscription_plan: SubscriptionPlan;
  billing_cycle: 'monthly' | 'annual';
  payment_status: PaymentStatus;
  videos_per_day: number;
  messages_per_video: number;
  max_video_duration: number;
  expiry_date: string;
  start_date: string;
}

export interface UserUsage {
  videos_used: number;
  date: string;
}

export interface VideoMessageUsage {
  messages_used: number;
  video_id: string;
}

export const useSubscriptionService = () => {
  const { toast } = useToast();
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user's subscription details
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setUserSubscription(null);
          setIsLoading(false);
          return;
        }
        
        // Get user's subscription details
        const { data: subscriptionData, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
          
        if (error) {
          console.error('Error fetching subscription:', error);
          setUserSubscription(null);
        } else if (subscriptionData) {
          // Check if subscription is expired
          const expiryDate = new Date(subscriptionData.expiry_date);
          const isExpired = isBefore(expiryDate, new Date());
          
          const paymentStatus: PaymentStatus = isExpired ? 'expired' : subscriptionData.payment_status as PaymentStatus;
          
          setUserSubscription({
            ...subscriptionData,
            subscription_plan: subscriptionData.subscription_plan as SubscriptionPlan,
            billing_cycle: subscriptionData.billing_cycle as 'monthly' | 'annual',
            payment_status: paymentStatus,
          });
        } else {
          // If no subscription, create a free trial
          const freeTrial = {
            user_id: user.id,
            email: user.email || '',
            subscription_plan: 'FREE' as SubscriptionPlan,
            billing_cycle: 'monthly' as 'monthly' | 'annual',
            payment_status: 'active' as PaymentStatus,
            videos_per_day: SUBSCRIPTION_PLANS.FREE.videosPerDay,
            messages_per_video: SUBSCRIPTION_PLANS.FREE.messagesPerVideo,
            max_video_duration: SUBSCRIPTION_PLANS.FREE.maxVideoDuration,
            start_date: new Date().toISOString(),
            expiry_date: addDays(new Date(), 3).toISOString() // 3-day free trial
          };
          
          // Insert free trial subscription
          const { data: newSubscription, error: insertError } = await supabase
            .from('user_subscriptions')
            .insert(freeTrial)
            .select()
            .single();
            
          if (insertError) {
            console.error('Error creating free trial:', insertError);
          } else if (newSubscription) {
            setUserSubscription(newSubscription as unknown as UserSubscription);
          }
        }
      } catch (error) {
        console.error('Error in fetchSubscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  // Get today's video usage for current user
  const getTodaysVideoUsage = async (): Promise<number> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('user_usage')
        .select('videos_used')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching video usage:', error);
        return 0;
      }
      
      return data?.videos_used || 0;
    } catch (error) {
      console.error('Error in getTodaysVideoUsage:', error);
      return 0;
    }
  };

  // Get message usage for a specific video
  const getVideoMessageUsage = async (videoId: string): Promise<number> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const { data, error } = await supabase
        .from('video_message_usage')
        .select('messages_used')
        .eq('user_id', user.id)
        .eq('video_id', videoId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching message usage:', error);
        return 0;
      }
      
      return data?.messages_used || 0;
    } catch (error) {
      console.error('Error in getVideoMessageUsage:', error);
      return 0;
    }
  };

  // Check if user can process a new video based on their subscription
  const canProcessVideo = async (durationMinutes: number): Promise<boolean> => {
    try {
      if (isLoading) return false;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to process videos",
          variant: "destructive",
        });
        return false;
      }
      
      // If no subscription or expired, show error
      if (!userSubscription || userSubscription.payment_status !== 'active') {
        toast({
          title: "Subscription required",
          description: "Please subscribe to process videos",
          variant: "destructive",
        });
        return false;
      }
      
      // Check video duration limit
      if (durationMinutes > userSubscription.max_video_duration) {
        toast({
          title: "Video too long",
          description: `Your plan only supports videos up to ${userSubscription.max_video_duration} minutes`,
          variant: "destructive",
        });
        return false;
      }
      
      // Check daily video limit
      const videosUsed = await getTodaysVideoUsage();
      if (videosUsed >= userSubscription.videos_per_day) {
        toast({
          title: "Daily limit reached",
          description: `You've reached your daily limit of ${userSubscription.videos_per_day} videos`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in canProcessVideo:', error);
      toast({
        title: "Error",
        description: "Failed to check subscription limits",
        variant: "destructive",
      });
      return false;
    }
  };

  // Check if user can send more messages for this video
  const canSendMessage = async (videoId: string): Promise<boolean> => {
    try {
      if (isLoading) return false;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to send messages",
          variant: "destructive",
        });
        return false;
      }
      
      // If no subscription or expired, show error
      if (!userSubscription || userSubscription.payment_status !== 'active') {
        toast({
          title: "Subscription required",
          description: "Please subscribe to send messages",
          variant: "destructive",
        });
        return false;
      }
      
      // Check message limit for this video
      const messagesUsed = await getVideoMessageUsage(videoId);
      if (messagesUsed >= userSubscription.messages_per_video) {
        toast({
          title: "Message limit reached",
          description: `You've reached your limit of ${userSubscription.messages_per_video} messages for this video`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in canSendMessage:', error);
      toast({
        title: "Error",
        description: "Failed to check subscription limits",
        variant: "destructive",
      });
      return false;
    }
  };

  // Increment video usage count
  const incrementVideoUsage = async (): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Check if usage record exists for today
      const { data: existingUsage, error: fetchError } = await supabase
        .from('user_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();
        
      if (fetchError) {
        console.error('Error fetching usage:', fetchError);
        return;
      }
      
      if (existingUsage) {
        // Update existing record
        await supabase
          .from('user_usage')
          .update({ videos_used: existingUsage.videos_used + 1 })
          .eq('id', existingUsage.id);
      } else {
        // Create new record
        await supabase
          .from('user_usage')
          .insert({
            user_id: user.id,
            date: today,
            videos_used: 1
          });
      }
    } catch (error) {
      console.error('Error incrementing video usage:', error);
    }
  };

  // Increment message usage for a video
  const incrementMessageUsage = async (videoId: string): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Check if usage record exists for this video
      const { data: existingUsage, error: fetchError } = await supabase
        .from('video_message_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('video_id', videoId)
        .maybeSingle();
        
      if (fetchError) {
        console.error('Error fetching message usage:', fetchError);
        return;
      }
      
      if (existingUsage) {
        // Update existing record
        await supabase
          .from('video_message_usage')
          .update({ messages_used: existingUsage.messages_used + 1 })
          .eq('id', existingUsage.id);
      } else {
        // Create new record
        await supabase
          .from('video_message_usage')
          .insert({
            user_id: user.id,
            video_id: videoId,
            messages_used: 1
          });
      }
    } catch (error) {
      console.error('Error incrementing message usage:', error);
    }
  };

  return {
    userSubscription,
    isLoading,
    canProcessVideo,
    canSendMessage,
    incrementVideoUsage,
    incrementMessageUsage,
  };
};
